import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  CreateTicketCommentExternalDto,
  CreateTicketCommentResponseDto,
  CreateTicketDto,
  TicketListDto,
  TicketWithDetailsDto,
} from '@sib/api';
import { DEFAULT_PAGE } from '@sib/api/constants';
import { QueryKey } from '@sib/api/enums';
import {
  InferSearchParamsQueryTypeWithArraySorter,
  PaginationOptionsWithSortArray,
} from '@sib/api/types';

const CACHE_TIME = 5000;

export const useOrganizationTicketsList = (
  organizationId: string,
  query: PaginationOptionsWithSortArray & { searchTerm?: string },
  options?: UseQueryOptions<TicketListDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.OrganizationTickets, organizationId, query],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.organizationTicketsControllerGetTickets(
        organizationId,
        query
      ),
  });

export const useInfiniteOrganizationTicketList = (
  payload: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.organization.organizationTicketsControllerGetTickets,
    1
  >,
  organizationId?: string,
  options?: Partial<
    UseInfiniteQueryOptions<TicketListDto, Error, InfiniteData<TicketListDto>>
  >
) =>
  useInfiniteQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.OrganizationTickets,
      organizationId,
      QueryKey.Operation.List,
      payload,
      QueryKey.Operation.Infinite,
    ],

    queryFn: async ({ pageParam }) => {
      const data =
        await api.organization.organizationTicketsControllerGetTickets(
          organizationId || '',
          {
            ...payload,
            page: pageParam as number,
          }
        );

      return { ...data, newItems: 0 };
    },

    getNextPageParam: lastPage => {
      const hasNextPage = lastPage.hasNextPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    placeholderData: keepPreviousData,
    initialPageParam: payload?.page || DEFAULT_PAGE,
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });

export type CreateOrganizationTicketDto = CreateTicketDto & {
  organizationId: string;
};

interface UseCreateOrganizationTicketOptions {
  options?: UseMutationOptions<
    TicketWithDetailsDto,
    Error,
    CreateOrganizationTicketDto
  >;
  isExtension?: boolean;
}

export const useCreateOrganizationTicket = ({
  options,
  isExtension = false,
}: UseCreateOrganizationTicketOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    mutationFn: async data =>
      api.organization.organizationTicketsControllerCreateTicket(
        data.organizationId,
        data
      ),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.OrganizationTickets,
          variables.organizationId,
        ],
      });
      if (isExtension) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.Tickets, QueryKey.Operation.List],
        });

        await queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.TicketOrganizationGroup],
        });

        await queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.TicketAssigneeGroup,
            QueryKey.Operation.List,
          ],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.TicketTypeGroup],
        });
      }
      if (variables.projectId)
        await queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.ProjectTickets, variables.projectId],
        });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useGetOneOrganizationTicket = (
  ticketId: string,
  organizaitonId: string,
  options?: Partial<
    UseQueryOptions<TicketWithDetailsDto, Error, TicketWithDetailsDto>
  >
) =>
  useQuery({
    ...options,
    enabled: !!ticketId || !!organizaitonId,
    queryKey: [QueryKey.Resource.OrganizationTickets, organizaitonId, ticketId],
    queryFn: async () =>
      api.organization.organizationTicketsControllerGetTicket(
        ticketId,
        organizaitonId
      ),
    placeholderData: keepPreviousData,
  });

export const useCreateOrganizationTicketComment = (
  organizationId: string,
  ticketId: string,
  options?: UseMutationOptions<
    CreateTicketCommentResponseDto,
    Error,
    CreateTicketCommentExternalDto
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    mutationFn: async data =>
      await api.organization.organizationTicketsControllerCreateExternalComment(
        ticketId,
        organizationId,
        data
      ),

    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.OrganizationTickets,
          organizationId,
          ticketId,
        ],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

type ApiOrganizationTicketsCsvExportQuery = Parameters<
  typeof api.organization.organizationTicketsControllerExportTickets
>[1];

export type OrganizationTicketsCsvExportQuery = Omit<
  ApiOrganizationTicketsCsvExportQuery,
  'columns'
> & {
  columns: string[];
};

export const exportOrganizationTicketsCsv = async (
  organizationId: string,
  query: OrganizationTicketsCsvExportQuery
) => {
  const serializedQuery: ApiOrganizationTicketsCsvExportQuery = {
    ...query,
    columns: JSON.stringify(query.columns),
  };

  return api.organization.organizationTicketsControllerExportTickets(
    organizationId,
    serializedQuery,
    { format: 'blob' }
  ) as Promise<Blob>;
};

export type UseExportOrganizationTicketsCsvOptions = UseMutationOptions<
  Blob,
  Error,
  OrganizationTicketsCsvExportQuery
> & {
  organizationId: string;
};

export const useExportOrganizationTicketsCsv = ({
  organizationId,
  ...options
}: UseExportOrganizationTicketsCsvOptions) =>
  useMutation({
    ...options,
    mutationFn: variables =>
      exportOrganizationTicketsCsv(organizationId, variables),
  });
